import classNames from 'classnames';
import React from 'react';
import { Link, LinkProps } from 'react-router';
import { notNil } from '../../core/helpers/is-nil';
import { useAccentBgContext } from '../accent-bg-context/accent-bg-context';
import { Icon } from '../icon/icon';
import { IconVariant } from '../icon/icon-data';
import { Typography } from '../typography/typography';
import styles from './button.module.scss';

type IconConfig = { hasIcon: boolean; variant: IconVariant; iconOnRightSide: boolean };

type ButtonContentProps = {
  children?: string;
  hasText: boolean;
  iconConfig: IconConfig | undefined;
};

const ButtonContent: React.FC<ButtonContentProps> = ({ iconConfig, children, hasText }) => {
  return (
    <>
      {iconConfig?.hasIcon && !iconConfig.iconOnRightSide ? (
        <Icon className={styles.icon} size="24" variant={iconConfig.variant} />
      ) : null}
      {hasText ? (
        <Typography className={styles.buttonText} tag="span" variant="buttontext">
          {children}
        </Typography>
      ) : null}
      {iconConfig?.hasIcon && iconConfig.iconOnRightSide ? (
        <Icon className={styles.icon} size="24" variant={iconConfig.variant} />
      ) : null}
    </>
  );
};

type BaseButtonProps = {
  children?: string;
  variant?: 'primary' | 'secondary';
  icon?: IconVariant;
  iconPosition?: 'left' | 'right';
};

type ButtonProps = BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  icon,
  iconPosition = 'left',
  children,
  disabled,
  className,
  ...rest
}) => {
  const { isAccentBackground } = useAccentBgContext();
  const hasIcon = notNil(icon);
  const hasText = notNil(children);
  const iconOnly = !hasText && hasIcon;

  const iconOnRightSide = hasIcon && iconPosition === 'right';

  const iconConfig: IconConfig | undefined = hasIcon ? { variant: icon, hasIcon, iconOnRightSide } : undefined;

  return (
    <button
      disabled={disabled}
      type="button"
      className={classNames(styles.root, styles[variant], className, {
        [styles.iconOnly]: iconOnly,
        [styles.disabled]: disabled,
        [styles.onAccentBg]: isAccentBackground,
      })}
      {...rest}
    >
      <ButtonContent hasText={hasText} iconConfig={iconConfig}>
        {children}
      </ButtonContent>
    </button>
  );
};

type ButtonLinkProps = { disabled?: boolean } & BaseButtonProps & Omit<LinkProps, 'children'>;

export const ButtonLink: React.FC<ButtonLinkProps> = ({
  variant = 'primary',
  icon,
  iconPosition = 'left',
  disabled = false,
  children,
  className,
  onClick,
  ...rest
}) => {
  const { isAccentBackground } = useAccentBgContext();
  const hasIcon = notNil(icon);
  const hasText = notNil(children);
  const iconOnly = !hasText && hasIcon;

  const iconOnRightSide = hasIcon && iconPosition === 'right';

  const iconConfig: IconConfig | undefined = hasIcon ? { variant: icon, hasIcon, iconOnRightSide } : undefined;

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (disabled) {
      event.preventDefault();
    } else {
      onClick?.(event);
    }
  };

  return (
    <Link
      className={classNames(
        styles.root,
        styles[variant],
        className,
        {
          [styles.iconOnly]: iconOnly,
          [styles.onAccentBg]: isAccentBackground,
          [styles.disabled]: disabled,
        },
        styles.linkReset,
      )}
      onClick={handleClick}
      {...rest}
    >
      <ButtonContent hasText={hasText} iconConfig={iconConfig}>
        {children}
      </ButtonContent>
    </Link>
  );
};
